import { useCallback, useEffect, useMemo, useState } from "react";

import { ApiError, api } from "./api/client";
import type {
  Building,
  County,
  PlanChangePreview,
  Status,
  TierId,
} from "./api/types";
import { useAuth } from "./auth/AuthProvider";
import { AdminPage } from "./components/AdminPage";
import { AgreementGate } from "./components/AgreementGate";
import { BuildingDrawer } from "./components/BuildingDrawer";
import { BuildingsTable } from "./components/BuildingsTable";
import { CountyChooser } from "./components/CountyChooser";
import { CoveragePage } from "./components/CoveragePage";
import { EmptyState } from "./components/EmptyState";
import { FilterRail } from "./components/FilterRail";
import { Footer } from "./components/Footer";
import { Gate } from "./components/Gate";
import { ForgotPassword, ResetPassword } from "./components/PasswordReset";
import { Hero } from "./components/Hero";
import { GUIDE_ROUTES, GuidePage } from "./components/GuidePage";
import { LandingPage } from "./components/LandingPage";
import { LEGAL_ROUTES, LegalPage } from "./components/LegalPage";
import { PlanChangeDialog } from "./components/PlanChangeDialog";
import { TopBar } from "./components/TopBar";
import {
  Link,
  Redirect,
  setSearchParam,
  usePathname,
  useSearchParam,
} from "./router";
import { AlertsPage } from "./components/AlertsPage";
import { ReportsPage } from "./components/ReportsPage";
import { UnitsTable } from "./components/UnitsTable";
import { Upsell } from "./components/Upsell";
import { useDebounced } from "./hooks/useDebounced";
import { useRows } from "./hooks/useRows";
import { alreadyRecorded, track } from "./lib/analytics";
import { planActionFor } from "./lib/billing";
import { countyName } from "./lib/counties";
import { EMPTY_FILTERS, type Filters, type View } from "./lib/filters";
import { fmt } from "./lib/format";
import { resolveRoute } from "./lib/route";
import { heldViews, resolveView, unavailableReason } from "./lib/views";

export function App() {
  const { user, ready, sessionEnd, signOut, acceptAgreement, refresh } =
    useAuth();
  const pathname = usePathname();
  const county = useSearchParam("county");

  // Legal pages resolve before the auth gate: the agreement has to be readable
  // by someone who has not signed up, and by property owners who never will.
  const legalSlug = LEGAL_ROUTES[pathname];
  if (legalSlug) return <LegalPage slug={legalSlug} />;

  const guideSlug = GUIDE_ROUTES[pathname];
  if (guideSlug) return <GuidePage slug={guideSlug} />;

  // Everything else — including whether a signed-out visitor gets the map or
  // the form — is decided in lib/route.ts, where it can be tested.
  const route = resolveRoute({
    pathname,
    county,
    ready,
    signedIn: Boolean(user),
    needsAcceptance: Boolean(user?.legal.needs_acceptance),
    mustChooseCounty: Boolean(user?.counties.must_choose),
    // Presentation only — the console's endpoints are refused to everyone else
    // by require_admin, whatever this says.
    isAdmin: user?.role === "admin",
    sessionEnded: sessionEnd !== null,
  });

  switch (route.kind) {
    case "loading":
      return (
        <div className="gate">
          <div className="eyebrow">Loading</div>
        </div>
      );
    case "coverage":
      return <CoveragePage />;
    case "landing":
      return <LandingPage slug={route.slug} />;
    case "gate":
      return <Gate />;
    case "forgot":
      return <ForgotPassword />;
    case "reset":
      return <ResetPassword />;
    case "redirect":
      return <Redirect to={route.to} />;
    case "agreement":
      return user ? (
        <AgreementGate
          legal={user.legal}
          onAccepted={() => acceptAgreement(user.legal.current_version)}
          onSignOut={signOut}
        />
      ) : null;
    case "choose-county":
      return (
        <>
          <TopBar county={county} />
          <CountyChooser title="Which county should your plan cover?" />
          <Footer agreementVersion={user?.legal.current_version} />
        </>
      );
    case "alerts":
      return (
        <>
          <TopBar county={county} />
          <AlertsPage />
          <Footer agreementVersion={user?.legal.current_version} />
        </>
      );
    case "reports":
      return (
        <>
          <TopBar county={county} />
          {/* Straight off the URL: the basket has to survive a reload, a Back
              and a shared link, so the filters it prices live there rather
              than in state. The selected county already works this way. */}
          <ReportsPage
            params={Object.fromEntries(
              new URLSearchParams(window.location.search),
            )}
          />
          <Footer agreementVersion={user?.legal.current_version} />
        </>
      );
    case "admin":
      return (
        <>
          <TopBar county={county} />
          <AdminPage />
          <Footer agreementVersion={user?.legal.current_version} />
        </>
      );
    case "dashboard":
      return <Dashboard onBillingReturn={refresh} />;
  }
}

function Dashboard({ onBillingReturn }: { onBillingReturn: () => void }) {
  const { user } = useAuth();
  const [status, setStatus] = useState<Status | null>(null);
  const [counties, setCounties] = useState<County[]>([]);
  // What the user asked for. The county may not be able to answer it, so the
  // view actually rendered is `view` below, resolved against what it holds.
  const [requestedView, setView] = useState<View>("buildings");
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  // The county lives in the URL, not in filter state: it is what the coverage
  // map navigates to, and a county view has to survive a reload and a Back.
  const county = useSearchParam("county");
  const [selected, setSelected] = useState<Building | null>(null);
  const [busyTier, setBusyTier] = useState<TierId | null>(null);
  const [actionError, setActionError] = useState("");
  const [movingCounty, setMovingCounty] = useState(false);
  // The plan change is two steps: a preview that charges nothing, then a
  // confirmation that does.
  const [pendingChange, setPendingChange] = useState<PlanChangePreview | null>(
    null,
  );
  const [changingPlan, setChangingPlan] = useState(false);
  const [changeError, setChangeError] = useState("");
  // Saved alerts, for the masthead badge and the rail's cap. Only fetched for
  // plans that can hold them — the route 402s otherwise, and a failed request
  // per dashboard load would be noise in the logs for every Preview visit.
  const [alerts, setAlerts] = useState<{ count: number; unread: number }>({
    count: 0,
    unread: 0,
  });

  // Typing in the search box shouldn't fire a county-wide query per keystroke.
  const debouncedSearch = useDebounced(filters.search);
  // A County subscription reads its own county whatever the URL says; asking
  // for another one is refused by the server, so the client does not ask.
  const scope = user?.counties.limit === 1 ? (user.counties.held ?? "") : county;
  const query = useMemo<Filters>(
    () => ({ ...filters, search: debouncedSearch, county: scope }),
    [filters, debouncedSearch, scope],
  );

  // The registry entry for the open county, which is what knows whether it has
  // buildings, parcels or only one of the two.
  const entry = counties.find((candidate) => candidate.fips === scope);
  const shapes = heldViews(entry);
  const view = resolveView(requestedView, entry);

  const { data, page, loading, error } = useRows(view, query);

  // Re-read on county change: the masthead figures *and the freshness* are
  // scoped to whatever county is open, so a stale global count would sit above
  // a filtered table — and, since counties are pulled separately, "updated an
  // hour ago" over a county that has not been pulled at all would be worse
  // than a wrong count. With no county open the freshness is the oldest across
  // the live counties, so one fresh county cannot mask three stale ones.
  useEffect(() => {
    const controller = new AbortController();
    api.status(scope, controller.signal).then(setStatus).catch(() => {});
    return () => controller.abort();
  }, [scope]);

  const loadAlerts = useCallback(async (signal?: AbortSignal) => {
    if (!user?.capabilities.can_watch) return;
    try {
      const list = await api.watches(signal);
      setAlerts({ count: list.watches.length, unread: list.total_new });
    } catch {
      // A badge is not worth an error banner; the alerts page reports its own.
    }
  }, [user?.capabilities.can_watch]);

  useEffect(() => {
    const controller = new AbortController();
    void loadAlerts(controller.signal);
    return () => controller.abort();
  }, [loadAlerts]);

  // The registry, for what each county publishes. Fetched once — it changes
  // when a county is added, not when one is selected.
  useEffect(() => {
    const controller = new AbortController();
    api
      .counties(controller.signal)
      .then((response) => setCounties(response.counties))
      .catch(() => {});
    return () => controller.abort();
  }, []);

  // Stripe sends the customer back with ?checkout=done. The webhook may land
  // a moment later, so re-read the user rather than trusting the redirect.
  //
  // The same URL names the plan, the amount and Stripe's session id — for the
  // ad platforms, not for entitlement, which the webhook decides. Recorded
  // once per session id: a reload of this URL is not a second sale.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") !== "done") return;
    window.history.replaceState({}, "", window.location.pathname);
    const session = params.get("session") ?? "";
    // A free trial is not a sale: nothing was charged, and reporting a $29
    // purchase to the ad platforms would teach them the wrong thing.
    if (session && !params.get("trial") && !alreadyRecorded(session)) {
      track({
        name: "purchase",
        kind: "subscription",
        tier: params.get("tier") ?? undefined,
        amountCents: Number(params.get("amount")) || 0,
        transactionId: session,
      });
    }
    onBillingReturn();
  }, [onBillingReturn]);

  /**
   * Saves the current search as an alert.
   *
   * The county sent is `scope`, not the raw URL parameter: a County
   * subscriber reads their own county whatever the URL says, and storing the
   * URL's value would create an alert pointing at a county the server will
   * refuse to answer.
   */
  const saveAlert = useCallback(
    async (name: string) => {
      await api.createWatch({
        name,
        scope: view,
        county_fips: scope,
        filters:
          view === "buildings"
            ? {
                milestone_only: filters.milestone_only,
                unsafe_only: filters.unsafe_only,
                min_score: filters.min_score,
                min_rate: filters.min_rate,
                search: filters.search.trim(),
                order: filters.order,
              }
            : {
                min_score: filters.min_score,
                flag: filters.flag,
                search: filters.search.trim(),
              },
      });
      await loadAlerts();
    },
    [view, scope, filters, loadAlerts],
  );

  const setFilter = useCallback(
    <K extends keyof Filters>(key: K, value: Filters[K]) =>
      setFilters((previous) => ({ ...previous, [key]: value })),
    [],
  );

  /**
   * Picking a plan means one of two entirely different things, and the
   * difference is money: a new subscriber goes to Stripe's checkout, while
   * someone who already subscribes has their existing subscription repriced.
   * Sending a subscriber to checkout would charge them a second full plan
   * while the first kept renewing, so the branch lives in `planActionFor`
   * with tests rather than inline here.
   */
  async function upgrade(tier: TierId) {
    setActionError("");
    const action = planActionFor(user, tier);
    if (action === "current") return;

    setBusyTier(tier);
    try {
      if (action === "change") {
        // A preview charges nothing. The customer confirms a figure they have
        // seen rather than the idea of one.
        setChangeError("");
        setPendingChange(await api.previewPlanChange(tier));
        setBusyTier(null);
        return;
      }
      const { url } = await api.checkout(tier);
      track({ name: "begin_checkout", tier });
      window.location.href = url;
    } catch (cause) {
      setActionError(
        cause instanceof ApiError ? cause.message : "Couldn't start checkout.",
      );
      setBusyTier(null);
    }
  }

  async function confirmPlanChange() {
    if (!pendingChange) return;
    setChangeError("");
    setChangingPlan(true);
    try {
      await api.changePlan(pendingChange.tier);
      setPendingChange(null);
      // Re-read rather than assume: the webhook for our own change lands a
      // moment later, and the server is what decides the tier either way.
      onBillingReturn();
    } catch (cause) {
      setChangeError(
        cause instanceof ApiError ? cause.message : "Couldn't switch plans.",
      );
    } finally {
      setChangingPlan(false);
    }
  }

  if (!user) return null;
  // A plan limited to one county: the county comes from the subscription, not
  // from the URL, so the whole view follows it.
  const single = user.counties.limit === 1;
  const held = user.counties.held ?? "";
  const noun = view === "buildings" ? "buildings" : "parcels";

  return (
    <>
      <TopBar county={scope} newAlerts={alerts.unread} />
      <Hero
        status={status}
        county={scope}
        entry={entry}
        counties={counties}
        view={view}
        filters={filters}
      />

      {movingCounty && (
        <div className="chooser-inline">
          <CountyChooser
            title="Move your plan to another county"
            onChosen={() => setMovingCounty(false)}
            onCancel={() => setMovingCounty(false)}
          />
        </div>
      )}

      <div className="shell">
        <FilterRail
          view={view}
          filters={filters}
          capabilities={user.capabilities}
          onChange={setFilter}
          onSaveAlert={saveAlert}
          alertCount={alerts.count}
          countyName={countyName(scope)}
        />

        <main className="main">
          {/* A tab the open county has no rows for is disabled rather than
              hidden: a missing tab reads as a feature we do not have, where a
              disabled one with its reason attached says which county it is
              about. See lib/views.ts. */}
          <div className="tabs" role="tablist">
            <button
              className="tab"
              role="tab"
              aria-selected={view === "buildings"}
              disabled={!shapes.buildings}
              title={unavailableReason("buildings", entry) ?? undefined}
              onClick={() => setView("buildings")}
            >
              Buildings
            </button>
            <button
              className="tab"
              role="tab"
              aria-selected={view === "units"}
              disabled={!shapes.units}
              title={unavailableReason("units", entry) ?? undefined}
              onClick={() => setView("units")}
            >
              Parcels
            </button>
          </div>

          {(error || actionError) && (
            <div className="err" role="alert">
              {actionError || error}
            </div>
          )}

          <div className="toolbar">
            <span className="count">
              {fmt(page?.total ?? 0)} {noun}
              {page?.capped ? " · preview is limited to the worst 10" : ""}
            </span>
            <div className="spacer" />
            <span className="scope">
              {/* A County plan has exactly one county and cannot clear it —
                  offering "all counties" to someone who does not hold them
                  would be an upsell disguised as a control. */}
              {single ? (
                <>
                  <b>{countyName(held) || held} County</b>
                  <button
                    className="linkbtn"
                    onClick={() => setMovingCounty(true)}
                  >
                    Change county
                  </button>
                </>
              ) : county ? (
                <>
                  {/* Named from the URL, not from the rows: a county that
                      returns nothing still has to say which county it was. */}
                  <b>{countyName(county) || county} County</b>
                  <button
                    className="linkbtn"
                    onClick={() => setSearchParam("county", "")}
                  >
                    Clear
                  </button>
                </>
              ) : (
                <b>All covered counties</b>
              )}
              <Link to="/coverage" className="linkbtn">
                Coverage map
              </Link>
            </span>
          </div>

          {!loading && data.rows.length === 0 && !error ? (
            // Three different empty tables, and they used to share one message
            // that guessed at the cause. The registry knows which this is: a
            // shape the county holds none of, a county-wide blank, or filters
            // that matched nothing.
            unavailableReason(view, entry) ? (
              <EmptyState
                title={`No ${view === "buildings" ? "buildings" : "parcels"} in ${entry?.name ?? countyName(scope) ?? "this county"} County`}
                hint={unavailableReason(view, entry) ?? ""}
              />
            ) : scope ? (
              <EmptyState
                title={`Nothing here for ${countyName(scope) || scope} County`}
                hint="No row matches these filters. Loosen one, or check the coverage map for what this county holds."
              />
            ) : (
              <EmptyState />
            )
          ) : (
            <div className={loading ? "loading-rows" : undefined}>
              {data.view === "buildings" ? (
                <BuildingsTable
                  rows={data.rows}
                  onSelect={setSelected}
                  canSeeFolio={user.capabilities.can_see_folio}
                />
              ) : (
                <UnitsTable rows={data.rows} />
              )}
            </div>
          )}

          {/* Rendered for a County subscriber too, not only for Preview —
              otherwise the only plan they can reach is the one they hold. */}
          <Upsell current={user.tier} onChoose={upgrade} busyTier={busyTier} />
        </main>
      </div>

      {pendingChange && (
        <PlanChangeDialog
          preview={pendingChange}
          busy={changingPlan}
          error={changeError}
          onConfirm={confirmPlanChange}
          onCancel={() => setPendingChange(null)}
        />
      )}

      <Footer agreementVersion={user.legal.current_version} />

      {selected && (
        <BuildingDrawer
          building={selected}
          canSeeUnits={user.capabilities.can_see_units}
          canSeeFolio={user.capabilities.can_see_folio}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}
